import React from 'react';
import { useTranslation } from 'react-i18next';
import { Client, ContractsProps } from './types';
import { COL_EXCLUDED, COL_TARGET } from '../../Tools/DndSelector/types';
import {
  DndColumnSetup,
  DndSelectorConfig,
  DndSelectorOutput,
  DndToolbarConfig
} from '../../Tools/DndSelector/types';
import DndSelector from '../../Tools/DndSelector/DndSelector';

const ClientsAndContracts = ({
  onChange,
  initialAuthorisedContracts = [],
  initialExcludedContracts = [],
  initialAuthorisedCustomers = [],
  initialExcludedCustomers = [],
  target = 'profile',
  data = [],
  titles,
  readOnly = false,
  height,
  customersOnly = false
}: ContractsProps) => {
  const { t } = useTranslation();

  // Consultation : une seule colonne, aucune règle de dépôt ni bouton d'édition à câbler.
  const readOnlyColumnConfig: DndColumnSetup = {
    numColumns: 1,
    titles: { target: titles?.target ?? t('clients/contracts') },
    emptyMessages: { [COL_TARGET]: t('perimeters.noClientsContracts') }
  };

  const editableColumnConfig: DndColumnSetup = {
    numColumns: target === 'profile' ? 3 : 2,
    titles:
      titles ??
      (target === 'profile'
        ? {
            target: t('authorized'),
            source: t('clients/contracts'),
            excluded: t('unauthorized')
          }
        : {
            source: t('source'),
            target: t('clients/contracts')
          }),
    emptyMessages: {
      target: ({ columnItemCounts }) =>
        columnItemCounts[COL_EXCLUDED] === 0 ? t('allClientsCurrentlyAuthorized') : undefined
    },
    getIsDropDisabled: (columnId, { activeParentId, columnItemIds }) => {
      if (columnId !== COL_EXCLUDED) return false;
      const targetIds = columnItemIds[COL_TARGET] ?? [];
      return targetIds.length > 0 && !targetIds.includes(activeParentId ?? -1);
    }
  };

  const readOnlyToolbarConfig: DndToolbarConfig = {
    title: titles?.toolbarTitle,
    closeAllTooltip: t('closeAllClientsContracts')
  };

  const editableToolbarConfig: DndToolbarConfig = {
    title: titles?.toolbarTitle ?? t('equipmentConfiguration.authorizations'),
    resetTooltip: t('resetClientsContracts'),
    closeAllTooltip: t('closeAllClientsContracts'),
    deselectTooltip: t('deselectAllClientsContracts')
  };

  const config: DndSelectorConfig<Client> = {
    data,
    // Sans enfants, chaque client devient un élément atomique du sélecteur (mode « flat ») :
    // on ne peut ni l'ouvrir ni en déposer un contrat.
    itemConfig: customersOnly
      ? { getId: (c) => c.id, getLabel: (c) => c.oss_code }
      : {
          getId: (c) => c.id,
          getLabel: (c) => c.oss_code,
          getChildren: (c) =>
            c.contracts.map((ct) => ({
              id: ct.id,
              primaryLabel: ct.oss_code,
              secondaryLabel: ct.oss_label
            })),
          getChildrenLength: (c) => c.contractsLength
        },
    columnConfig: readOnly ? readOnlyColumnConfig : editableColumnConfig,
    initial: {
      targetParents: initialAuthorisedCustomers,
      excludedParents: initialExcludedCustomers,
      targetChildren: initialAuthorisedContracts,
      excludedChildren: initialExcludedContracts
    },
    toolbarConfig: readOnly ? readOnlyToolbarConfig : editableToolbarConfig
  };

  const handleChange = (output: DndSelectorOutput) => {
    onChange?.({
      authorisedCustomers: output.byColumn[COL_TARGET]?.fullParentIds ?? [],
      authorisedContracts: output.byColumn[COL_TARGET]?.partialChildIds ?? [],
      excludedCustomers: output.byColumn[COL_EXCLUDED]?.fullParentIds ?? [],
      excludedContracts: output.byColumn[COL_EXCLUDED]?.partialChildIds ?? [],
      target
    });
  };

  return (
    <DndSelector
      config={config}
      onChange={readOnly ? undefined : handleChange}
      isLoading={data.length === 0}
      readOnly={readOnly}
      height={height}
    />
  );
};

export default ClientsAndContracts;
